
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { DateTime } from 'luxon';
import { findSupportedLocale } from '@/utils/i18n-helpers';
import { decodeCustomQuestion } from '@/utils/decoding';
import IconButton from '@/components/shared/suites/IconButton.vue';
import SelectComponent from '@/components/shared/Select.vue';

const namespace: string = 'orders';

@Component<CustomQuestions>({
	components: {
		IconButton,
		SelectComponent,
		ValidationProvider
	}
})
export default class CustomQuestions extends Vue {
	@Action('setCheckoutAnsweredQuestions', { namespace }) private setCheckoutAnsweredQuestions!: (answeredQuestions: AnsweredCustomQuestion[]) => void;
	@Action('removeCustomField', { namespace: 'customQuestions' }) private removeCustomField!: (index: number) => void;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Getter('getCheckoutAnsweredQuestions', { namespace }) private checkoutAnsweredQuestions!: AnsweredCustomQuestion[] | null;
	@Getter('getModifyingOrder', { namespace }) private isModifyingOrder!: boolean;
	@Getter('getTakeoutInformation', { namespace }) private pickupInformation!: Pickup;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Prop({ type: Array, required: true }) private questions!: CustomQuestion[];
	@Prop({ type: Boolean, default: false }) private removable!: boolean;
	@Watch('questions')
	onQuestionsChange(): void {
		this.initializeQuestions();
	}
	@Watch('pickupInformation.dueByDate')
	@Watch('pickupInformation.dueByTime')
	onDueByTimeOrDateChange(): void {
		this.updateTimepickerQuestions();
	}

	private mutableQuestions: CustomQuestion[] = [];

	private get answeredQuestions(): CheckoutCustomQuestion[] {
		return this.mutableQuestions
			.filter((questionObj: CustomQuestion) => {
				// If not required, checkbox questions are always considered as answered (unchecked is an answer)
				if (questionObj.question_type === 'checkbox') {
					return 'checked' in questionObj;
				}
				if (questionObj.question_type === 'timepicker') {
					return questionObj.timeSelected !== '';
				}
				if (questionObj.question_type === 'open') {
					return questionObj.answer !== '';
				}
				return questionObj.answerObj && Object.keys(questionObj.answerObj).length;
			})
			.map(this.formatAnsweredQuestionObj);
	}

	private mounted(): void {
		this.initializeQuestions();
	}

	/**
	 * Initialize mutable questions from the questions prop, restoring any previously answered values.
	 *
	 * @return {void}
	 */
	private initializeQuestions(): void {
		if (this.questions?.length) {
			this.mutableQuestions = this.questions.map((questionObj: CustomQuestion) => {
				questionObj = decodeCustomQuestion(questionObj);
				let answeredQuestion: CheckoutCustomQuestion | undefined = this.checkoutAnsweredQuestions?.find((answeredQuestionObj) => answeredQuestionObj.question === questionObj.question);

				if (questionObj.question_type === 'checkbox') {
					return {
						...questionObj,
						checked: answeredQuestion?.answer === this.$t('orders.guest_details.requests.custom_questions.checked')
					};
				}

				if (questionObj.question_type === 'timepicker') {
					return {
						...questionObj,
						timeSelected: answeredQuestion?.answer || '',
						disabled: this.isModifyingOrder
					};
				}

				if (questionObj.question_type === 'open') {
					return {
						...questionObj,
						answer: answeredQuestion?.answer || ''
					};
				}

				if (answeredQuestion) {
					const index = questionObj.options?.findIndex((option: string) => answeredQuestion!.answer === option);
					return {
						...questionObj,
						answerObj: {
							index,
							label: this.$options.filters?.localize(answeredQuestion, 'answer')
						}
					};
				}
				return { ...questionObj };
			});
			this.updateTimepickerQuestions(true);
		}
	}

	/**
	 * Set the options of the timepicker questions if possible.
	 * On first load and if the question initially has a time selected, set the options as just that answer to prevent edge cases.
	 *
	 * @param {boolean} [isFirstLoad=false]
	 * @return {void}
	 */
	private updateTimepickerQuestions(isFirstLoad: boolean = false): void {
		this.mutableQuestions.forEach((questionObj: CustomQuestion) => {
			if (questionObj.question_type === 'timepicker' && (!questionObj.disabled || isFirstLoad)) {
				const { dueByTime, dueByDate } = this.pickupInformation || {};
				const timeOptions = this.generateTimeOptions(dueByDate, dueByTime);
				Vue.set(
					questionObj,
					'timeOptions',
					isFirstLoad && questionObj.timeSelected
						? [{ label: DateTime.fromISO(questionObj.timeSelected).toFormat(this.$t('orders.guest_details.requests.custom_questions.time_format')), value: questionObj.timeSelected }]
						: timeOptions
				);
				if (dueByDate && dueByTime && !isFirstLoad) {
					questionObj.timeSelected = '';
					this.setCheckoutAnsweredQuestions(this.answeredQuestions);
				}
			}
		});
	}

	/**
	 * Return the localized options in an array of objects containing their indexes.
	 *
	 * @param {QuestionWithOptions} question
	 * @return {{ index: number; label: string; }[]}
	 */
	private formatDropdownOptions(question: QuestionWithOptions): { index: number; label: string; }[] {
		const localizedOptions: string[] | undefined = this.$options.filters?.localize(question, 'options');
		if (localizedOptions && localizedOptions.length) {
			const output = localizedOptions.map((questionOption: string, index: number) => {
				return {
					index,
					label: questionOption
				};
			});
			return output;
		}
		return question.options.map((questionOption: string, index: number) => {
			return {
				index,
				label: questionOption
			};
		});
	}

	/**
	 * Format the answered questions object depending of the question's type
	 *
	 * @param {CustomQuestion} questionObj
	 * @return {CheckoutCustomQuestion}
	 */
	private formatAnsweredQuestionObj(questionObj: CustomQuestion): CheckoutCustomQuestion {
		let answer: string;
		if (questionObj.question_type === 'checkbox') {
			const defaultLocale = this.restaurant.locales?.find((locale: RestaurantLocale) => locale.is_default);
			const supportedLocale = defaultLocale?.locale_short ? findSupportedLocale('', defaultLocale.locale_short) : null;
			const translationKey = `orders.guest_details.requests.custom_questions.${questionObj.checked ? 'checked' : 'unchecked'}`;

			answer = supportedLocale ? this.$t(translationKey, supportedLocale.code) : this.$t(translationKey);
		}

		else if (questionObj.question_type === 'timepicker') {
			answer = questionObj.timeSelected;
		}
		else if (questionObj.question_type === 'open') {
			answer = questionObj.answer;
		}
		else {
			const index = questionObj.answerObj?.index as number;
			answer = questionObj.options[index];
		}

		const answeredQuestion: CheckoutCustomQuestion = {
			question: questionObj.question,
			answer,
			...(questionObj.is_internal && { is_internal: true }),
			...(questionObj.question_type === 'timepicker' && { question_type: 'timepicker' })
		};

		const localization = {} as Localization;
		if (questionObj.localization && Object.keys(questionObj.localization).length) {
			for (const locale in questionObj.localization) {
				if (questionObj.question_type === 'checkbox') {
					const supportedLocale = findSupportedLocale('', locale);
					localization[locale] = {
						question: questionObj.localization[locale].question,
						answer: this.$t(`orders.guest_details.requests.custom_questions.${questionObj.checked ? 'checked' : 'unchecked'}`, supportedLocale!.code)
					};
				}
				else if (questionObj.question_type === 'timepicker') {
					localization[locale] = {
						question: questionObj.localization[locale].question
					};
				}
				else if (questionObj.question_type === 'open') {
					localization[locale] = {
						question: questionObj.localization[locale].question,
						answer: questionObj.answer
					};
				}
				else {
					const index = questionObj.answerObj?.index as number;
					localization[locale] = {
						question: questionObj.localization[locale].question,
						answer: questionObj.localization[locale].options![index]
					};
				}
			}
			answeredQuestion.localization = localization;
		}

		return answeredQuestion;
	}

	/**
	 * Generate time options for the timepicker question.
	 * The options range from 12 hours before and 12 hours after the given date/time, in 15-minute intervals.
	 *
	 * @param {string} [pickupDateString]
	 * @param {string} [pickupTimeString]
	 * @return {{ label: string; value: string; }[]}
	 */
	private generateTimeOptions(pickupDateString?: string, pickupTimeString?: string): { label: string; value: string; }[] {
		let startTime: DateTime;
		const pickupDate: DateTime | undefined = pickupDateString ? DateTime.fromISO(pickupDateString) : undefined;
		const pickupTime: DateTime | undefined = pickupTimeString ? DateTime.fromFormat(pickupTimeString, this.$t('orders.guest_details.takeout.timepicker_format')) : undefined;
		let now: DateTime = DateTime.now();
		const roundedMinutes = Math.ceil(now.minute / 15) * 15;
		now = now.set({ minute: roundedMinutes });
		if (roundedMinutes === 60) {
			now = now.plus({ hours: 1 }).set({ minute: 0 });
		}

		const startDate = DateTime.fromObject({
			year: pickupDate?.year || now.year,
			month: pickupDate?.month || now.month,
			day: pickupDate?.day || now.day,
			hour: pickupTime?.hour || now.hour,
			minute: pickupTime?.minute || now.minute,
			second: 0,
			millisecond: 0
		});

		startTime = startDate.minus({ hours: 12 });
		const timeSeries: { label: string; value: string; }[] = [];

		for (let i = 0; i < 24 * 4; i++) {
			timeSeries.push({
				label: startTime.toFormat(this.$t('orders.guest_details.requests.custom_questions.time_format')),
				value: startTime.toISO()
			});
			startTime = startTime.plus({ minutes: 15 });
		}

		return timeSeries;
	}

	/**
	 * Toggle the timepicker question's disabled state.
	 * If we enable the question, generate the options and reset the time selected.
	 *
	 * @param {number} questionIndex
	 * @return {void}
	 */
	private toggleTimepickerLock(questionIndex: number): void {
		const timepickerQuestion = this.mutableQuestions[questionIndex] as QuestionWithTimePicker;
		timepickerQuestion.disabled = !timepickerQuestion.disabled;
		if (!timepickerQuestion.disabled) {
			const { dueByDate, dueByTime } = this.pickupInformation || {};
			timepickerQuestion.timeOptions = this.generateTimeOptions(dueByDate, dueByTime);
			if (dueByDate && dueByTime) {
				timepickerQuestion.timeSelected = '';
				this.setCheckoutAnsweredQuestions(this.answeredQuestions);
			}
		}
	}

	/**
	 * Build the validation rules for a custom question.
	 *
	 * @param {CustomQuestion} question
	 * @return {string|object}
	 */
	private customQuestionRules(question: CustomQuestion): string | object {
		if (question.question_type === 'checkbox') {
			return question.required ? { required: { allowFalse: false } } : '';
		}
		const rules: string[] = [];
		if (question.required) {
			rules.push('required');
		}
		if (question.question_type === 'open') {
			rules.push('max:480');
		}
		return rules.join('|');
	}

	/**
	 * Remove the question locally and from the custom fields store, and sync the answered questions.
	 * The local splice/sync runs first because removing the last field unmounts this component
	 * before the questions watcher can fire.
	 *
	 * @param {number} questionIndex
	 * @return {void}
	 */
	private onQuestionRemoved(questionIndex: number): void {
		this.mutableQuestions.splice(questionIndex, 1);
		this.setCheckoutAnsweredQuestions(this.answeredQuestions);
		this.removeCustomField(questionIndex);
	}
}
